import { useMemo } from "react";
import HC from "@/lib/compute";
import { MODE } from "@/lib/ui";
import { Select } from "@/components/dash/Bits";
import { HeroCard, RespCard, MetaCard } from "./score/TopCards";
import { ProfileCard } from "./score/ProfileCard";
import { IndCard, ParCard } from "./score/Matrices";
import { ProjectsCard } from "./score/ProjectsCard";
import { ClosureGate, isClosureApproved } from "./score/ClosureGate";

function useScope(st) {
  const fyOpts = HC.fyOptions();
  const fy = st.fy && fyOpts.includes(st.fy) ? st.fy : "";
  const ridOpts = HC.ridOptions(fy);
  const rid = st.rid && ridOpts.includes(st.rid) ? st.rid : "";
  const projOpts = HC.projectsFor(fy, rid);
  const p = st.p && projOpts.includes(st.p) ? st.p : "";
  return { fyOpts, ridOpts, projOpts, f: { fy, rid, p, tool: st.tool, ind: st.ind, intv: st.intv } };
}

function useOptions(projOpts, p, tool) {
  return useMemo(() => {
    const PS = new Set(projOpts);
    const base = HC.M.comb.filter(r => (p ? r.p === p : PS.has(r.p)));
    return {
      toolOpts: [...new Set(base.map(r => r.tool).filter(Boolean))].sort(HC.toolSort),
      indOpts: [...new Set(base.map(r => r.ind).filter(Boolean))].sort((a, b) => HC.seqOf(a) - HC.seqOf(b)),
      intvOpts: [...new Set(base.filter(r => (!tool || r.tool === tool)).map(r => r.intv).filter(Boolean))].sort(),
    };
  }, [projOpts, p, tool]);
}

export default function ProjectScore({ st, set }) {
  const { fyOpts, ridOpts, projOpts, f } = useScope(st);
  const { toolOpts, indOpts, intvOpts } = useOptions(projOpts, f.p, f.tool);
  const o = useMemo(() => HC.oecd(f, MODE), [f.fy, f.rid, f.p, f.tool, f.ind, f.intv]); // eslint-disable-line react-hooks/exhaustive-deps
  const projLabel = p => [p, p + ((HC.M.ipBy[p] || {}).partner ? " · " + HC.M.ipBy[p].partner.slice(0, 26) : "")];
  const closureOk = isClosureApproved(f.p);
  return (
    <>
      <div className="filters">
        <Select id="f-fy" label="Financial year" opts={fyOpts} sel={f.fy} onChange={v => set({ fy: v, rid: "", p: "" })} />
        <Select id="f-rid" label="Research agency" opts={ridOpts} sel={f.rid} onChange={v => set({ rid: v, p: "" })} />
        <Select id="f-p" label="Project" opts={projOpts.map(projLabel)} sel={f.p} allLabel="All projects" onChange={v => set({ p: v })} />
        <Select id="f-tool" label="Tool" opts={toolOpts} sel={f.tool} onChange={v => set({ tool: v, intv: "" })} />
        <Select id="f-ind" label="Indicator" opts={indOpts} sel={f.ind} onChange={v => set({ ind: v })} />
        <Select id="f-intv" label="Intervention" opts={intvOpts} sel={f.intv} onChange={v => set({ intv: v })} />
        <button className="btn" id="f-reset" data-testid="f-reset" onClick={() => set({ fy: "", rid: "", p: "", tool: "", ind: "", intv: "", pf: ["", "", "", ""] })}>Reset</button>
      </div>
      {!closureOk ? (
        <div className="grid">
          <ClosureGate p={f.p} onPickAnother={() => set({ p: "" })} />
        </div>
      ) : (
        <div className="grid">
          <HeroCard o={o} f={f} />
          <RespCard o={o} f={f} />
          <MetaCard o={o} f={f} />
          <ProfileCard o={o} />
          <IndCard o={o} />
          <ParCard o={o} />
          {o.projRows.length ? <ProjectsCard o={o} f={f} pf={st.pf} set={set} /> : null}
        </div>
      )}
    </>
  );
}
